import React, {createContext, useEffect, useRef, useState} from 'react';
import {GoogleAuthProvider, onAuthStateChanged, signInWithPopup, signOut as firebaseSignOut} from 'firebase/auth';
import {getFCMToken} from '../lib/fcm';
import type {AuthContextType, User} from '../types/auth';
import {auth} from '../config/firebase';
import {NotificationTokenType, useRegisterFcmTokenMutation, useRegisterLoginMutation} from '../generated/graphql.tsx';
import {
  StaffAccessRefusedError,
  StaffEmailDomainError,
  clearStaffAccessRefusal,
  rememberStaffAccessRefusal,
  staffAccessRefusal
} from '../lib/staffAccessRefusal';

export const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [registerLoginMutation] = useRegisterLoginMutation();
  const [registerFcmTokenMutation] = useRegisterFcmTokenMutation();
  const pendingRegisterLogin = useRef<Promise<void> | null>(null);

  const signOutAfterRefusal = async () => {
    try {
      await firebaseSignOut(auth);
      sessionStorage.removeItem('yojo_tenancy');
    } catch (signOutError) {
      console.error('Failed to sign out after a refused login:', signOutError);
    }
  };

  const sendRegisterLogin = async () => {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (!timezone) {
      console.error('Failed to detect timezone');
      return;
    }

    console.log('Registering login with timezone:', timezone);

    try {
      await registerLoginMutation({
        variables: { timezone }
      });
      // An accepted login ends any refusal this tab was still holding (e.g. the person was added
      // and signed in from another tab); other errors are left alone
      clearStaffAccessRefusal();
      setError(prevError => prevError instanceof StaffAccessRefusedError ? null : prevError);

      // Update user state with timezone after successful registration
      setUser(prevUser => prevUser ? {
        ...prevUser,
        timezone
      } : null);
    } catch (error) {
      // YOJO-3123 [self-registered staff-domain email gets analyst]: the backend refuses a person
      // with no staff account (403 + its sentence; staffAccessRefusal decides). Sign them out and hand
      // the sentence to the login screen. The 403 only gets this far after apollo.ts has refreshed the
      // token and retried once.
      const refusal = staffAccessRefusal(error);
      if (refusal) {
        // State first: the spinner and the user must not wait for a sign-out that may hang or fail
        setUser(null);
        setError(refusal);
        setLoading(false);
        // Also kept in sessionStorage: it must survive apollo.ts's hard redirect to /login
        rememberStaffAccessRefusal(refusal.message);
        void signOutAfterRefusal();
        throw refusal;
      }

      console.error('Failed to register login:', error);
      // Non-blocking error - don't throw
    }
  };

  // A fresh sign-in registers the login from two places (the auth listener and signInWithGoogle).
  // They share ONE in-flight request, so both get the same outcome: a refusal can never reach one
  // caller while the other carries on with a different error.
  const registerLogin = (): Promise<void> => {
    if (!pendingRegisterLogin.current) {
      pendingRegisterLogin.current = sendRegisterLogin().finally(() => {
        pendingRegisterLogin.current = null;
      });
    }
    return pendingRegisterLogin.current;
  };

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      clearTimeout(timeoutId); // Cancel timeout if Firebase responds

      if (firebaseUser) {
        setUser({
          id: firebaseUser.uid,
          email: firebaseUser.email,
          displayName: firebaseUser.displayName,
          photoUrl: firebaseUser.photoURL
        });
        try {
          await registerLogin();
        } catch {
          // Refused login: registerLogin has already cleared the user, set the error and started the sign-out
        }
      } else {
        setUser(null);
      }
      setLoading(false);
    });

    // Fallback: If Firebase doesn't respond in 5 seconds, assume no user
    // This handles cases where Firebase fails to initialize (incognito mode, network issues, etc.)
    timeoutId = setTimeout(() => {
      console.warn('[Auth] Firebase initialization timeout - assuming no user');
      setUser(null);
      setLoading(false);
    }, 5000);

    return () => {
      clearTimeout(timeoutId);
      unsubscribe();
    };
  }, []);

  const signInWithGoogle = async () => {
    try {
      setError(null);
      clearStaffAccessRefusal();
      // Ensure tenant ID is set
      auth.tenantId = import.meta.env.VITE_TENANT_ID;
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);

      // Validate email domain
      const email = result.user.email;
      const allowedDomains = ['@luminapain.com', '@yojo.health'];
      if (!email || !allowedDomains.some(domain => email.toLowerCase().endsWith(domain))) {
        await firebaseSignOut(auth);
        throw new StaffEmailDomainError();
      }

      // First register the login
      await registerLogin();

      // Wait 50ms before proceeding with FCM token
      await new Promise(resolve => setTimeout(resolve, 50));

      // Then register FCM token
      console.log('Registering FCM token');
      const fcmToken = await getFCMToken();
      if (fcmToken) {
        console.log('FCM token obtained:', fcmToken);
        try {
          await registerFcmTokenMutation({
            variables: {
              input: {
                token: fcmToken,
                type: NotificationTokenType.Firebase
              }
            }
          });
        } catch (error) {
          console.error('Failed to register FCM token:', error);
          // Non-blocking error - don't throw
        }
      }

    } catch (err: any) {
      if (err?.code === 'auth/operation-not-allowed') {
        setError(new Error('Google sign-in is not enabled. Please contact support.'));
      } else if (err?.code === 'auth/popup-closed-by-user') {
        setError(new Error('Sign-in was cancelled. Please try again.'));
      } else {
        setError(err as Error);
      }
      throw err;
    }
  };

  const signOut = async () => {
    try {
      sessionStorage.removeItem('yojo_tenancy');
      await firebaseSignOut(auth);
    } catch (err) {
      setError(err as Error);
      throw err;
    }
  };

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      error,
      isAuthenticated: !!user,
      signInWithGoogle,
      signOut
    }}>
      {children}
    </AuthContext.Provider>
  );
};
